/**
 * The retro kit's location name (slice 6b): the scene's name in a `corner`
 * panel at the top left, shown on each arrival (`arrival` counts them: the
 * first view, a door, the way back from the dungeon) for
 * `uiTiming.locationMs`, then faded out (at once with `prefers-reduced-motion:
 * reduce`). Nothing else: no region line, no description.
 */
import { type CSSProperties, useEffect, useState } from 'react';
import { Panel } from './panel.tsx';
import { uiTiming } from './timing.ts';
import './ui.css';

export type LocationNameProps = {
  /** The scene's name, in the page's language. */
  readonly name: string;
  /** Bumped on every arrival; each bump shows the name again. */
  readonly arrival: number;
};

/** The fade's length, a variable so a reduced-motion preference can drop it in CSS (`ui.css`). */
const fade = { '--ab-fade-ms': `${uiTiming.panelFadeMs}ms` } as CSSProperties;

export const LocationName = ({ name, arrival }: LocationNameProps) => {
  const [shown, setShown] = useState(true);
  useEffect(() => {
    setShown(true);
    const timer = window.setTimeout(() => {
      setShown(false);
    }, uiTiming.locationMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [arrival]);
  return (
    <Panel placement="corner" className="ab-location" data-testid="location-name" data-shown={shown} aria-hidden={!shown} style={fade}>
      {name}
    </Panel>
  );
};
